package dev.lingvo.site.components.studio.editor

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import dev.lingvo.shared.DeviceClass
import dev.lingvo.shared.screenshots.ScreenshotEngine
import dev.lingvo.shared.screenshots.doc.BackgroundLayer
import dev.lingvo.shared.screenshots.doc.ColorRefs
import dev.lingvo.shared.screenshots.doc.DeviceLayer
import dev.lingvo.shared.screenshots.doc.FrameFinish
import dev.lingvo.shared.screenshots.doc.FrameKind
import dev.lingvo.shared.screenshots.doc.GroupLayer
import dev.lingvo.shared.screenshots.doc.ImageLayer
import dev.lingvo.shared.screenshots.doc.Paint
import dev.lingvo.shared.screenshots.doc.Shadow
import dev.lingvo.shared.screenshots.doc.ShapeLayer
import dev.lingvo.shared.screenshots.doc.Stop
import dev.lingvo.shared.screenshots.doc.TextLayer
import dev.lingvo.site.ds.LSelect
import dev.lingvo.site.ds.LSelectOption
import dev.lingvo.site.ds.LTextField
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr

/** Fill edits carry a complete validated paint, so each stop commit is one history entry. */
@Composable
internal fun FillProperties(state: StudioUiState, dispatch: (StudioIntent) -> Unit) {
    val layer = state.selected.singleOrNull() ?: return
    val paint = when (layer) {
        is ShapeLayer -> layer.fill
        is BackgroundLayer -> layer.fill
        is TextLayer -> layer.style.color
        else -> return
    } ?: Paint.Solid("@accent")
    val enabled = state.editable && (layer is BackgroundLayer || !layer.base.locked)
    fun save(value: Paint) { dispatch(StudioIntent.Property(state.selection, LayerProperty.Fill(value))) }
    StudioDisclosure(t("studio.editor.layer_properties.fill", "Fill")) {
        val type = when (paint) { is Paint.Solid -> "solid"; is Paint.Linear -> "linear"; is Paint.Radial -> "radial" }
        LSelect(type, listOf(LSelectOption("solid", t("studio.editor.layer_properties.solid", "Solid")),
            LSelectOption("linear", t("studio.editor.layer_properties.linear_gradient", "Linear gradient")), LSelectOption("radial", t("studio.editor.layer_properties.radial_gradient", "Radial gradient"))),
            { value -> save(when (value) {
                "linear" -> Paint.Linear(180.0, listOf(Stop(0.0, "@bg"), Stop(1.0, "@accent")))
                "radial" -> Paint.Radial(.5, .5, .5, listOf(Stop(0.0, "@bg"), Stop(1.0, "@accent")))
                else -> Paint.Solid("@accent")
            }) }, label = t("studio.editor.layer_properties.fill_type", "Fill type"), enabled = enabled)
        if (paint is Paint.Solid) ColorProperty(t("studio.editor.layer_properties.colour", "Colour"), paint.color, enabled) { save(Paint.Solid(it)) }
        else {
            val stops = when (paint) { is Paint.Linear -> paint.stops; is Paint.Radial -> paint.stops; else -> emptyList() }
            fun changeStops(next: List<Stop>) { save(when (paint) {
                is Paint.Linear -> paint.copy(stops = next.sortedBy { it.at })
                is Paint.Radial -> paint.copy(stops = next.sortedBy { it.at })
                else -> paint
            }) }
            if (paint is Paint.Linear) StudioNumericProperty(t("studio.editor.layer_properties.angle", "Angle"), paint.angle, enabled) { save(paint.copy(angle = it)) }
            if (paint is Paint.Radial) {
                StudioNumericProperty(t("studio.editor.layer_properties.centre_x", "Centre X"), paint.cx, enabled) { save(paint.copy(cx = it.coerceIn(0.0, 1.0))) }
                StudioNumericProperty(t("studio.editor.layer_properties.centre_y", "Centre Y"), paint.cy, enabled) { save(paint.copy(cy = it.coerceIn(0.0, 1.0))) }
            }
            stops.forEachIndexed { index, stop ->
                ColorProperty(tf("studio.stop_colour", "Stop {number} colour", "number" to index + 1), stop.color, enabled) { color ->
                    changeStops(stops.mapIndexed { i, item -> if (i == index) item.copy(color = color) else item })
                }
                StudioNumericProperty(tf("studio.stop_position", "Stop {number} position", "number" to index + 1), stop.at, enabled) { at ->
                    changeStops(stops.mapIndexed { i, item -> if (i == index) item.copy(at = at.coerceIn(0.0, 1.0)) else item })
                }
                StudioButton(t("studio.editor.layer_properties.remove_stop", "Remove stop"), enabled && stops.size > 2) { changeStops(stops.filterIndexed { i, _ -> i != index }) }
            }
            StudioButton(t("studio.editor.layer_properties.add_stop", "Add stop"), enabled && stops.size < 8) {
                val gap = stops.zipWithNext().maxBy { (a, b) -> b.at - a.at }
                changeStops(stops + Stop((gap.first.at + gap.second.at) / 2, "@accent"))
            }
        }
    }
}

/** Shadow presets and radius are ordinary immutable layer properties rather than canvas filters per frame. */
@Composable
internal fun EffectProperties(state: StudioUiState, dispatch: (StudioIntent) -> Unit) {
    val layer = state.selected.singleOrNull() ?: return
    val enabled = state.editable && !layer.base.locked
    StudioDisclosure(t("studio.editor.layer_properties.effects", "Effects")) {
        StudioNumericProperty(t("studio.editor.layer_properties.opacity", "Opacity"), layer.base.opacity, enabled) {
            dispatch(StudioIntent.Property(state.selection, LayerProperty.Opacity(it.coerceIn(0.0, 1.0))))
        }
        val radius = when (layer) { is ShapeLayer -> layer.cornerRadius; is ImageLayer -> layer.cornerRadius; is GroupLayer -> layer.cornerRadius; else -> null }
        radius?.let { value -> StudioNumericProperty(t("studio.editor.layer_properties.corner_radius", "Corner radius"), value, enabled) {
            dispatch(StudioIntent.Property(state.selection, LayerProperty.Radius(it.coerceAtLeast(0.0))))
        } }
        StudioButton(t("studio.editor.layer_properties.no_shadow", "No shadow"), enabled) { dispatch(StudioIntent.Property(state.selection, LayerProperty.Shadow(null))) }
        StudioButton(t("studio.editor.layer_properties.soft_shadow", "Soft shadow"), enabled) { dispatch(StudioIntent.Property(state.selection, LayerProperty.Shadow(Shadow(0.0, 12.0, 32.0, "#00000040")))) }
        StudioButton(t("studio.editor.layer_properties.lifted_shadow", "Lifted shadow"), enabled) { dispatch(StudioIntent.Property(state.selection, LayerProperty.Shadow(Shadow(0.0, 24.0, 64.0, "#00000066")))) }
    }
}

/** The picker uses the engine's compatible silhouettes instead of offering impossible frames. */
@Composable
internal fun DeviceProperties(state: StudioUiState, dispatch: (StudioIntent) -> Unit) {
    val layer = state.selected.singleOrNull() as? DeviceLayer ?: return
    val enabled = state.editable && !layer.base.locked
    StudioDisclosure(t("studio.editor.layer_properties.device", "Device")) {
        val frames = DeviceClass.fromId(state.view.device)?.let { ScreenshotEngine.frames.offeredFor(it) }.orEmpty()
        LSelect(layer.frame.name, frames.map { LSelectOption(it.name, frameLabel(it)) },
            { dispatch(StudioIntent.Frame(layer.base.id, FrameKind.valueOf(it))) }, label = t("studio.editor.layer_properties.frame", "Frame"), enabled = enabled)
        LSelect(layer.finish.name, FrameFinish.entries.map { LSelectOption(it.name, finishLabel(it)) },
            { dispatch(StudioIntent.Property(state.selection, LayerProperty.Finish(FrameFinish.valueOf(it)))) },
            label = t("studio.editor.layer_properties.finish", "Finish"), enabled = enabled)
    }
}

@Composable
private fun ColorProperty(label: String, value: String, enabled: Boolean, commit: (String) -> Unit) {
    if (!enabled) { org.jetbrains.compose.web.dom.Text(value); return }
    var draft by remember(value) { mutableStateOf(value) }
    val parsed = if (draft.startsWith("#")) draft.uppercase() else draft
    LTextField(draft, { draft = it }, label = label, enabled = enabled)
    StudioButton(t("studio.editor.layer_properties.apply_colour", "Apply colour"), enabled && ColorRefs.isValid(parsed) && parsed != value) { commit(parsed) }
}

private fun frameLabel(frame: FrameKind): String = when (frame) {
    FrameKind.PHONE_ISLAND -> tr("studio.editor.layer_properties.phone_with_dynamic_island", "Phone with Dynamic Island")
    FrameKind.PHONE_NOTCH -> tr("studio.editor.layer_properties.phone_with_notch", "Phone with notch")
    FrameKind.PHONE_PUNCH_HOLE -> tr("studio.editor.layer_properties.phone_with_punch_hole", "Phone with punch hole")
    FrameKind.PHONE_FLAT -> tr("studio.editor.layer_properties.flat_phone", "Flat phone")
    FrameKind.TABLET_UNIFORM -> tr("studio.editor.layer_properties.tablet", "Tablet")
    FrameKind.TABLET_ANDROID -> tr("studio.editor.layer_properties.android_tablet", "Android tablet")
    FrameKind.FOLDABLE_OPEN -> tr("studio.editor.layer_properties.open_foldable", "Open foldable")
    FrameKind.WATCH_RECT -> tr("studio.editor.layer_properties.rectangular_watch", "Rectangular watch")
    FrameKind.WATCH_ROUND -> tr("studio.editor.layer_properties.round_watch", "Round watch")
    FrameKind.LAPTOP -> tr("studio.editor.layer_properties.laptop", "Laptop")
    FrameKind.BROWSER -> tr("studio.editor.layer_properties.browser", "Browser")
    FrameKind.TV -> tr("studio.editor.layer_properties.tv", "TV")
    FrameKind.NONE -> tr("studio.editor.layer_properties.no_frame", "No frame")
}
private fun finishLabel(finish: FrameFinish): String = when (finish) {
    FrameFinish.AUTO -> tr("studio.editor.layer_properties.automatic", "Automatic")
    FrameFinish.GRAPHITE -> tr("studio.editor.layer_properties.graphite", "Graphite")
    FrameFinish.SILVER -> tr("studio.editor.layer_properties.silver", "Silver")
    FrameFinish.MIDNIGHT -> tr("studio.editor.layer_properties.midnight", "Midnight")
    FrameFinish.SAND -> tr("studio.editor.layer_properties.sand", "Sand")
    FrameFinish.WHITE -> tr("studio.editor.layer_properties.white", "White")
}
